import * as React from 'react'
import {
  Flex,
  Skeleton,
  Text,
  Link,
  Button,
  DropdownMenu,
  IconButton,
  EllipsisVerticalIcon,
  Badge,
  Heading,
  toast,
  AlertDialog,
  Notice,
} from '@buffer-mono/popcorn'
import {
  type FragmentOf,
  graphql,
  readFragment,
  type ResultOf,
} from '~publish/graphql'
import styles from './PlanContent.module.css'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'
import { useMutation } from '@apollo/client'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { AccountForBillingQuery } from '../Billing'
import capitalize from 'lodash/capitalize'
import { isLegacyBilling } from './BillingSettings.helpers'
import type { BillingInterval, Product } from '~publish/gql/graphql'
import PlanCards from './PlanCards'
import { getAccountURL } from '~publish/legacy/utils/formatters/getURL'

const REMOVE_SUBSCRIPTION_SCHEDULE = graphql(/* GraphQL */ `
  mutation removeSubscriptionSchedule($organizationId: OrganizationId!) {
    removeSubscriptionSchedule(organizationId: $organizationId) {
      ... on RemoveSubscriptionScheduleResponse {
        success
      }
      ... on RemoveSubscriptionScheduleError {
        message
      }
    }
  }
`)

/**
 * Fragment for discount data. Applies to both OBBilling and MPBilling.
 */
export const Discount_Billing = graphql(/* GraphQL */ `
  fragment Discount_Billing on Billing {
    discount {
      gatewayId
      name
      type
      duration
      value
      durationInMonths
    }
  }
`)

/**
 * Fragment for upcoming invoice data.
 */
export const UpcomingInvoice_Billing = graphql(/* GraphQL */ `
  fragment UpcomingInvoice_Billing on Billing {
    upcomingInvoiceAmount
    upcomingInvoice {
      amountWithoutTax
      amountTax
      currency
    }
  }
`)

/**
 * Fragment for subscription and plan information.
 * Applies to OBBilling.
 */
export const PlanContent_Billing = graphql(
  /* GraphQL */ `
    fragment PlanContent_Billing on OBBilling {
      canStartTrial
      balance
      gateway {
        gatewayType
        gatewayId
        gatewayPlatform
      }
      paymentDetails {
        paymentMethod
      }
      ...Discount_Billing
      ...UpcomingInvoice_Billing
      pricingTiers {
        flatFee
        unitPrice
        upperBound
      }
      subscription {
        periodEnd
        isCanceledAtPeriodEnd
        isPaymentPastDue
        canceledAt
        trial {
          isActive
          remainingDays
          startDate
          endDate
        }
        plan {
          id
          name
          interval
          limits {
            channels
            members
          }
          prices {
            baseMonthlyPrice
            basePlanPrice
            monthlyFlatFee
            monthlyChannelPrice
            channelTierUnit
          }
        }
        scheduledUpdate {
          newPlan
          newInterval
          newQuantity
          effectiveDate
        }
      }
    }
  `,
  [Discount_Billing, UpcomingInvoice_Billing],
)

/**
 * Fragment for subscription and plan information for legacy billing.
 */
export const LegacyPlanContent_Billing = graphql(
  /* GraphQL */ `
    fragment LegacyPlanContent_Billing on MPBilling {
      canAccessPublishing
      canAccessAnalytics
      billingRedirectUrl
      balance
      paymentDetails {
        paymentMethod
      }
      ...Discount_Billing
      ...UpcomingInvoice_Billing
      subscriptions {
        plan
        product
        interval
        periodEnd
        isCanceledAtPeriodEnd
      }
    }
  `,
  [Discount_Billing, UpcomingInvoice_Billing],
)

/**
 * Fragment for organization data used on the billing page.
 */
export const OrganizationDataForBilling_AccountOrganization =
  graphql(/* GraphQL */ `
    fragment OrganizationDataForBilling_AccountOrganization on AccountOrganization {
      id
      members {
        totalCount
      }
      channels {
        id
        isLocked
      }
      limits {
        channels
      }
      commonTrackingProperties {
        atEventBillingState
        atEventBillingPlan
        atEventBillingCycle
        atEventBillingGateway
        atEventIsNewBuffer
        atEventChannelQty
      }
      billing {
        paymentDetails {
          hasPaymentDetails
        }
      }
    }
  `)

// TODO: check if used elsewhere
/**
 * Formats number to currency with 2 decimals and no decimal for whole numbers
 * @param {number} value number to format.
 * @return {string} formatted string
 */
export function formatCurrency(value: number): string {
  if (value) {
    if (Number.isInteger(value)) {
      return value.toString()
    } else {
      return value.toFixed(2)
    }
  } else {
    return '0'
  }
}

const getDiscountCopy = (
  discountData: FragmentOf<typeof Discount_Billing>,
): {
  discountBadgeCopy: string | null
  discountDescription: string | null
} => {
  const discount = readFragment(Discount_Billing, discountData).discount
  if (!discount) {
    return {
      discountBadgeCopy: null,
      discountDescription: null,
    }
  }

  let discountDescription = `${discount.value}% off`
  let discountBadgeCopy = `${discount.value}% off`
  if (discount.type === 'amount') {
    discountDescription = `$${formatCurrency(discount.value)}`
    discountBadgeCopy = `$${formatCurrency(discount.value)} off`
  }

  switch (discount.duration) {
    case 'forever':
      discountDescription += ' forever'
      break
    case 'once':
      discountDescription += ' on your next invoice'
      break
    case 'repeating':
      discountDescription += ` for ${discount.durationInMonths} months`
      break
  }
  return {
    discountBadgeCopy,
    discountDescription,
  }
}

/**
 * Formats the customer's credit balance copy if needed
 * @param creditBalance - The credit balance.
 * @returns The formatted credit balance copy if the customer does not have a balance of 0 in which case it returns null
 */
const getCreditBalanceCopy = (creditBalance: number): JSX.Element | null => {
  if (creditBalance === 0) return null

  // Positive balance: implies that the customer has a debit which will be applied to their next invoice
  if (creditBalance > 0) {
    return (
      <>
        <strong>${formatCurrency(creditBalance)} will be added </strong>
        to your upcoming invoice.
      </>
    )
  }

  // Negative balance: implies that the customer has a credit which will be applied to their next invoice
  return (
    <>
      You have a credit of{' '}
      <strong>${formatCurrency(Math.abs(creditBalance))}</strong> which will be
      automatically applied to your upcoming invoices.
    </>
  )
}

const getRenewalOrCancelDateCopy = (
  interval: BillingInterval,
  periodEnd: string | null,
  isCanceledAtPeriodEnd = false,
): JSX.Element => {
  return (
    <Text size="md">
      Billed {interval}ly.{' '}
      {periodEnd && (
        <>
          {isCanceledAtPeriodEnd ? 'Cancels on' : 'Renews on'}{' '}
          <strong>
            {new Date(periodEnd).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
            .
          </strong>
        </>
      )}
    </Text>
  )
}

/**
 * Formats the legacy plan name.
 * @param product - The product of the plan.
 * @returns The formatted legacy plan name.
 */
const getLegacyPlanName = (product: Product): string => {
  return `${product} Plan`
}

const LegacyPlanCard = ({
  name,
  product,
  interval,
  periodEnd,
  isCanceledAtPeriodEnd,
}: {
  name: string
  product: Product
  interval: BillingInterval
  periodEnd: string | null
  isCanceledAtPeriodEnd: boolean
}): JSX.Element => {
  return (
    <Flex className={styles.legacyPlanCard} fullWidth>
      <Flex direction="column" className={styles.legacyPlanInfo}>
        <Heading size="medium" className={styles.legacyPlanName}>
          {getLegacyPlanName(product)}
        </Heading>

        {name.toLowerCase() === 'free' ? (
          <Text size="md">
            You are on a Legacy Buffer plan. Update to get access.
          </Text>
        ) : (
          getRenewalOrCancelDateCopy(interval, periodEnd, isCanceledAtPeriodEnd)
        )}
      </Flex>
      {!isCanceledAtPeriodEnd && (
        <Button
          as="a"
          // We reuse the legacy rpc functionality in the old billing page to cancel the plan
          href={`${getAccountURL()}/billing?cancelPlan=true&product=${product}`}
          variant="critical"
          disabled={true}
        >
          Cancel Plan
        </Button>
      )}
    </Flex>
  )
}

/**
 * Renders the legacy plans card.
 * @param loading - Whether the data is loading.
 * @returns The rendered legacy plans card.
 */
const LegacyPlansList = ({
  billingData,
}: {
  billingData: FragmentOf<typeof LegacyPlanContent_Billing>
}): JSX.Element => {
  const billing = readFragment(LegacyPlanContent_Billing, billingData)
  return (
    <Flex direction="column" gap="lg" fullWidth>
      {billing.subscriptions.map((subscription) => {
        return (
          <LegacyPlanCard
            key={`${subscription.plan}-${subscription.product}`}
            name={subscription.plan}
            product={subscription.product}
            interval={subscription.interval}
            periodEnd={subscription.periodEnd}
            isCanceledAtPeriodEnd={subscription.isCanceledAtPeriodEnd}
          />
        )
      })}
    </Flex>
  )
}

/**
 * Formats a number to a currency string.
 * @param amount - The amount to format.
 * @param currency - The currency to format the amount in.
 * @returns The formatted currency string.
 */
export const numberWithCurrency = (
  amount: number,
  currency: string,
): string => {
  const locale = navigator.language || 'en-EN'
  return amount.toLocaleString(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
  })
}

const openChurnSurvey = (isOnTrial: boolean): void => {
  if (window.appshell?.actions) {
    const { actions } = window.appshell || {}
    actions.openModal(window.appshell.MODALS.churnSurvey, {
      isOnTrial,
    })
  }
}

const openConfirmCancellationModal = (
  subscriptionType: 'plan' | 'trial',
): void => {
  if (window.appshell?.actions) {
    const { actions } = window.appshell || {}
    actions.openModal(window.appshell.MODALS.confirmCancellation, {
      subscriptionType,
    })
  }
}

const handleCancelPlan = ({
  isOnTrial,
  organization,
}: {
  isOnTrial: boolean
  organization: ResultOf<
    typeof OrganizationDataForBilling_AccountOrganization
  > | null
}): void => {
  if (!organization) {
    return
  }
  if (isOnTrial) {
    openConfirmCancellationModal('trial')
  } else {
    openChurnSurvey(isOnTrial)
  }

  BufferTracker.cancelPlanClicked({
    organizationId: organization.id,
    ...organization.commonTrackingProperties,
  })
}

const openTieredPricingModal = (cta: string): void => {
  const { MODALS, actions } = window?.appshell || {}
  actions.openModal(MODALS.quantityUpdateTieredPricing, {
    cta,
    upgradePathName: 'account-upgrade',
  })
}

const openSubscriptionUpdate = (cta: string): void => {
  const { MODALS, actions } = window?.appshell || {}
  actions.openModal(MODALS.subscriptionUpdate, {
    cta,
    upgradePathName: 'account-upgrade',
    shouldPickModalOnOrganizationState: true,
  })
}

const openPlanSelector = (
  cta: string,
  upgradePathName = 'billing-upgrade',
): void => {
  const { MODALS, actions } = window?.appshell || {}
  actions.openModal(MODALS.planSelector, {
    cta,
    upgradePathName,
  })
}

const RemoveSubscriptionScheduleAlertDialog = ({
  currentPlanName,
  currentChannelCount,
  children,
}: {
  currentPlanName: string
  currentChannelCount: number
  children: React.ReactNode
}): JSX.Element => {
  const organizationId = useOrganizationId()
  const [removeSubscriptionSchedule, { loading: isRemoveScheduleLoading }] =
    useMutation(REMOVE_SUBSCRIPTION_SCHEDULE, {
      onCompleted: (data) => {
        if (
          data?.removeSubscriptionSchedule?.__typename ===
            'RemoveSubscriptionScheduleResponse' &&
          data?.removeSubscriptionSchedule?.success
        ) {
          toast.success('Plan reverted successfully.')
        }
        if (
          data?.removeSubscriptionSchedule?.__typename ===
          'RemoveSubscriptionScheduleError'
        ) {
          toast.error(
            `Error removing scheduled update. Please try again or contact support.`,
          )
        }
      },
      onError: () => {
        toast.error(
          `Error removing scheduled update. Please try again or contact support.`,
        )
      },
      refetchQueries: [AccountForBillingQuery],
    })

  return (
    <AlertDialog>
      <AlertDialog.Trigger>{children}</AlertDialog.Trigger>
      <AlertDialog.Content>
        <AlertDialog.Title>
          Revert to your previous Buffer plan
        </AlertDialog.Title>
        <AlertDialog.Description>
          Are you sure you want to revert to your previous subscription of the{' '}
          <b>{currentPlanName}</b> plan with <b>{currentChannelCount}</b>{' '}
          channels?
        </AlertDialog.Description>
        <AlertDialog.Actions>
          <AlertDialog.Cancel>
            <Button variant="secondary">Cancel</Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button
              variant="critical"
              onClick={(): void => {
                removeSubscriptionSchedule({
                  variables: {
                    organizationId: organizationId ?? '',
                  },
                })
              }}
              loading={isRemoveScheduleLoading}
            >
              Confirm
            </Button>
          </AlertDialog.Action>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}

/**
 * A smart component that renders either a list or a single text element based on the number of items.
 */
const PlanDetailsList = ({
  children,
}: {
  children: React.ReactNode[]
}): JSX.Element => {
  // Filter out falsy values (null, undefined, false) to get actual item count
  const items = React.Children.toArray(children).filter(Boolean)

  if (items.length === 0) {
    return <></>
  }

  if (items.length === 1) {
    // For a single item, extract the content from inside the li
    const singleItem = items[0] as React.ReactElement
    return <>{singleItem.props.children}</>
  }

  return <ul className={styles.planCardList}>{items}</ul>
}

/**
 * Renders the plan card for OBBilling.
 * @param data - The billing data to render.
 * @returns The rendered plan card.
 */
const Content = ({
  billingData,
  organizationData,
}: {
  billingData: FragmentOf<typeof PlanContent_Billing>
  organizationData: FragmentOf<
    typeof OrganizationDataForBilling_AccountOrganization
  > | null
}): JSX.Element => {
  // Read fragments
  const billing = readFragment(PlanContent_Billing, billingData)
  const organization = readFragment(
    OrganizationDataForBilling_AccountOrganization,
    organizationData,
  )
  const subscription = billing.subscription

  // Get copy
  const { discountBadgeCopy, discountDescription } = getDiscountCopy(billing)

  const creditBalance = billing.balance
  const creditBalanceCopy = getCreditBalanceCopy(creditBalance)

  const channelsLimit = organization?.limits.channels

  // Billing states
  const isOnTrial = subscription.trial?.isActive ?? false
  const isOnTrialEndDate = subscription.trial?.endDate
    ? new Date(subscription.trial?.endDate).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null
  // Non-agency tiered pricing has more than 2 pricing tiers
  const isOnNonAgencyTieredPricing = billing.pricingTiers.length > 2
  const isPayingWithBank = billing.paymentDetails.paymentMethod === 'bank'
  const isFreePlan = subscription.plan.id === 'free'
  const renewalDate = subscription.periodEnd
    ? new Date(subscription.periodEnd).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  const canceledDate = subscription.canceledAt
    ? new Date(subscription.canceledAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  // TODO: make this non-nullable in the schema. We should never have a null subscription
  if (!subscription) {
    return <Skeleton show={true} />
  }

  const handleChangePlan = (): void => {
    if (isOnTrial) {
      openPlanSelector('billing-plansCard-changePlanFromTrial-1')
    } else if (isFreePlan) {
      openPlanSelector('billing-plansCard-upgrade-1')
    } else {
      openPlanSelector('billing-plansCard-changePlanFromPaid-1')
    }
  }

  const handleAddRemoveChannels = (): void => {
    if (isOnNonAgencyTieredPricing) {
      openTieredPricingModal('billing-plansCard-addRemoveChannels-1')
    } else {
      openSubscriptionUpdate('billing-plansCard-addRemoveChannels-1')
    }
  }

  const openBeaconChat = (): void => {
    const helpCenterUrl =
      'https://support.buffer.com/?openContact=true&utm_source=buffer&utm_medium=billing-support-link&utm_campaign=billing-support'
    if (window.Beacon) {
      window.Beacon('navigate', '/ask/message/')
      window.Beacon('open')
    } else {
      window.open(helpCenterUrl, '_blank')
    }
  }

  return (
    <Flex direction="column" gap="sm" fullWidth>
      {isPayingWithBank && !isOnNonAgencyTieredPricing && (
        <Notice variant="neutral">
          <Text>
            To make changes to your plan, please contact support at{' '}
            <Link href="mailto:support@buffer.com">support@buffer.com</Link>.
          </Text>
        </Notice>
      )}
      {isOnNonAgencyTieredPricing && (
        <Notice variant="neutral">
          <Text>
            You are on a custom Buffer pricing plan. To make changes, please
            contact support at{' '}
            <Link href="mailto:support@buffer.com">support@buffer.com</Link>.
          </Text>
        </Notice>
      )}
      {/* Header */}
      <Flex fullWidth className={styles.container}>
        {/* Plan details */}
        <Flex direction="column" gap="xs">
          {/* Plan name and trial status */}
          <Flex direction="row" gap="xs" align="center">
            <Heading size="medium">Plan:</Heading>
            <Badge
              size="mid"
              variant="brand-xsubtle"
              style={{ textTransform: 'uppercase' }}
            >
              {subscription.plan.name}
            </Badge>
            {isOnTrial && (
              <Badge
                size="mid"
                variant="neutral"
                style={{ textTransform: 'uppercase' }}
              >
                Trial
              </Badge>
            )}
          </Flex>
          {/* Renewal date and other details */}
          <PlanDetailsList>
            {/* Paid plans canceled at period end */}
            {subscription.isCanceledAtPeriodEnd && (
              <li>
                <Text size="md">
                  {canceledDate && `Your plan was changed on ${canceledDate}. `}
                  You can continue using the{' '}
                  <strong>{subscription.plan.name}</strong> features until the
                  end of your billing cycle on <strong>{renewalDate}</strong>,
                  when your Free subscription starts.
                </Text>
              </li>
            )}
            {/* Paid plans with renewal dates */}
            {!subscription.isCanceledAtPeriodEnd &&
              !isFreePlan &&
              !isOnTrial && (
                <li>
                  {getRenewalOrCancelDateCopy(
                    subscription.plan.interval,
                    subscription.periodEnd,
                  )}
                </li>
              )}
            {/* Free plan */}
            {isFreePlan && (
              <li>
                <Text size="md" className={styles.freePlanText}>
                  The Free plan is limited to 3 channels. Upgrade to the
                  Essentials plan and connect as many channels as you&apos;d
                  like.
                </Text>
              </li>
            )}
            {/* Trial */}
            {isOnTrial && (
              <li>
                <Text size="md">
                  Your free trial ends in{' '}
                  <strong>{subscription.trial?.remainingDays} days</strong> on{' '}
                  <strong>{isOnTrialEndDate}</strong>.
                </Text>
              </li>
            )}
            {/* Discounts */}
            {discountBadgeCopy && (
              <li>
                <Text size="md">
                  You have a {discountDescription} discount applied.
                </Text>
              </li>
            )}
            {/* Credit balance */}
            {creditBalanceCopy && (
              <li>
                <Text size="md">{creditBalanceCopy}</Text>
              </li>
            )}
          </PlanDetailsList>
        </Flex>
        {/* Actions */}
        {!isPayingWithBank && (
          <Flex direction="row" gap="2xs" align="center">
            {/* Change plan button hidden for non-agency tiered pricing */}
            {!isOnNonAgencyTieredPricing && (
              <Button
                size="medium"
                variant="secondary"
                onClick={handleChangePlan}
              >
                Change Plan
              </Button>
            )}
            {!isFreePlan && (
              <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                  <IconButton label="More" size="medium" variant="secondary">
                    <EllipsisVerticalIcon />
                  </IconButton>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end">
                  {subscription.scheduledUpdate && (
                    <RemoveSubscriptionScheduleAlertDialog
                      currentPlanName={capitalize(subscription.plan.name)}
                      currentChannelCount={channelsLimit ?? 0}
                    >
                      <DropdownMenu.Item
                        onSelect={(e): void => e.preventDefault()}
                      >
                        Revert To Previous Plan
                      </DropdownMenu.Item>
                    </RemoveSubscriptionScheduleAlertDialog>
                  )}
                  <DropdownMenu.Item onClick={handleAddRemoveChannels}>
                    Add/Remove Channels
                  </DropdownMenu.Item>
                  {!subscription.isCanceledAtPeriodEnd && (
                    <DropdownMenu.Item
                      variant="critical"
                      onClick={(): void =>
                        handleCancelPlan({
                          isOnTrial,
                          organization,
                        })
                      }
                    >
                      Cancel {isOnTrial ? 'Trial' : 'Plan'}
                    </DropdownMenu.Item>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu>
            )}
          </Flex>
        )}
      </Flex>

      {/* 3-column plan details card */}
      <PlanCards
        billingData={billingData}
        organizationData={organizationData}
      />

      <Text size="md">
        Need help?{' '}
        <Link as="button" onClick={openBeaconChat}>
          Talk to our support team
        </Link>
        .
      </Text>
    </Flex>
  )
}

/**
 * Renders the plan content based on the billing data.
 * @param billingData - The billing data to render.
 * @param organizationData - The organization data to render.
 * @returns The rendered plan content.
 */
export const PlanContent = ({
  billingData,
  organizationData,
}: {
  billingData?:
    | FragmentOf<typeof PlanContent_Billing>
    | FragmentOf<typeof LegacyPlanContent_Billing>
    | null
  organizationData: FragmentOf<
    typeof OrganizationDataForBilling_AccountOrganization
  > | null
}): JSX.Element => {
  if (!billingData) {
    return <Skeleton show={true} />
  }

  return (
    <>
      {isLegacyBilling(billingData) ? (
        <LegacyPlansList billingData={billingData} />
      ) : (
        <Content
          billingData={billingData}
          organizationData={organizationData}
        />
      )}
    </>
  )
}

export default PlanContent
